import * as THREE from 'three';
import utils from '../../utils';
import constants from '../../config/Constants';
import settings from '../../config/Settings';
import planetsData from '../../config/PlanetsData';
import CustomConeGeometry from './CustomConeGeometry';

class Beam {
  constructor() {
    this.three = {
      beamGroup: this.createBeamGroup()
    }
  }

  createBeamGroup() {
    const shadowEarth = this.createShadowEarth()
    const beam = this.createBeam()
    const group = new THREE.Group()
    group.type = 'beam'
    group.beam = beam
    group.mainObject = shadowEarth
    group.add(shadowEarth)
    group.add(beam)
    group.translateX(planetsData.Earth.orbitRadius)
    return group
  }

  createBeam() {
    const deltaDistance = 300000 / constants.UD_TO_KM
    const angle = THREE.Math.degToRad(settings.beam.angleDeg)

    const beamMaterial = this.createBeamMaterial()
    const beamAnimationMaterial = this.createBeamAnimationMaterial()
    const edgeMaterial = this.createEdgeMaterial()

    // Create cone.
    const coneSlantHeight = deltaDistance
    const coneRadius = Math.sin(angle) * coneSlantHeight
    const coneHeight = Math.cos(angle) * coneSlantHeight
    const coneGeom = new CustomConeGeometry(coneRadius, coneHeight, 32)
    const cone = new THREE.Mesh(coneGeom, beamMaterial)

    // Add cone edge lines.
    var coneEdges = new THREE.EdgesGeometry(coneGeom);
    var coneEdgeLines = new THREE.LineSegments(coneEdges, edgeMaterial);

    // Create cap of the cone.
    const capGeom = new THREE.SphereGeometry(coneSlantHeight, 32, 128, 0, Math.PI * 2, 0, angle)
    const cap = new THREE.Mesh(capGeom, beamMaterial)

    // Add cap edge lines.
    var capEdges = new THREE.EdgesGeometry(capGeom);
    var capEdgeLines = new THREE.LineSegments(capEdges, edgeMaterial);
    const capGroup = new THREE.Group()

    // Group everything.
    const coneGroup = new THREE.Group()
    coneGroup.add(cone)
    coneGroup.add(coneEdgeLines)
    capGroup.add(cap)
    capGroup.add(capEdgeLines)

    // Add animated cap.
    const capAnimatedGeom = new THREE.SphereGeometry(coneSlantHeight, 32, 128, 0, Math.PI * 2, 0, angle)
    const capAnimated = new THREE.Mesh(capAnimatedGeom, beamAnimationMaterial)

    const beam = new THREE.Group()
    beam.capAnimated = capAnimated
    beam.add(capAnimated)
    beam.add(capGroup)
    beam.add(coneGroup)
    return beam;
  }

  createBeamMaterial() {
    return new THREE.MeshBasicMaterial({
      color: 'green',
      opacity: 0.2,
      side: THREE.DoubleSide,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    })
  }

  createEdgeMaterial() {
    return new THREE.LineBasicMaterial({
      color: 0xffffff,
      opacity: 0.1,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    })
  }

  createBeamAnimationMaterial() {
    return new THREE.MeshBasicMaterial({
      color: 'blue',
      opacity: 0.2,
      side: THREE.DoubleSide,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    })
  }

  pulseAnimation() {
    let scaleVector = new THREE.Vector3(0.001, 0.001, 0.001)
    if (this.three.beamGroup.beam.capAnimated.scale.x < 1) {
      scaleVector.copy(this.three.beamGroup.beam.capAnimated.scale)
      scaleVector.addScalar(0.01)
    }
    this.three.beamGroup.beam.capAnimated.scale.copy(scaleVector)
  }

  updateOrigin(spherePosition) {
    const lat = 44.456476
    const lon = -72.218070

    const sphereRadius = planetsData.Earth.radius

    const phi = (90 - lat) * (Math.PI / 180)
    const theta = (lon + 180) * (Math.PI / 180)

    const x = -sphereRadius * Math.sin(phi) * Math.cos(theta)
    const y = sphereRadius * Math.cos(phi)
    const z = sphereRadius * Math.sin(phi) * Math.sin(theta)

    this.three.beamGroup.beam.position.set(spherePosition.x + x, spherePosition.y + y, spherePosition.z + z)
    this.three.beamGroup.mainObject.position.copy(spherePosition)
  }

  createShadowEarth() {
    const earthData = planetsData.Earth
    const shadowEarth = utils.createSphere(earthData.radius, this.getShadowMaterial(earthData))
    return shadowEarth
  }

  getShadowMaterial(planetData) {
    // creating a texture with canvas
    var canvas = document.createElement('canvas'),
    ctx = canvas.getContext('2d');
    canvas.width = 64;
    canvas.height = 64;
    // drawing gray scale rectangle
    ctx.fillStyle = '#757575';
    ctx.fillRect(0, 0, 1024, 512);
    var texture = new THREE.CanvasTexture(canvas);

    const materialData = {
      map: new THREE.TextureLoader().load(planetData.materialMap),
      alphaMap: texture,
      transparent: true,
    }
    if (planetData.materialBumpMap) {
      materialData.bumpMap = new THREE.TextureLoader().load(planetData.materialBumpMap)
      materialData.bumpScale = planetData.materialBumpScale
    }
    return new THREE.MeshPhongMaterial(materialData)
  }

  setBroadcastAt(broadcastAt, rightAscension, declination) {
    // Reset bem scale and rotations.
     this.three.beamGroup.beam.rotation.set( 0, 0, 0 );
     this.three.beamGroup.beam.scale.set( 1, 1, 1 );
     this.three.beamGroup.beam.updateMatrix();

    // Scale and rotate beam.
    const deltaTimeSec = (Date.now() - broadcastAt) / 1000
    this.three.beamGroup.beam.scale.set(deltaTimeSec, deltaTimeSec, deltaTimeSec)
    this.three.beamGroup.beam.rotateY(rightAscension)
    this.three.beamGroup.beam.rotateX(declination)
  }

  hide() {
    this.three.beamGroup.visible = false;
  }

  show() {
    this.three.beamGroup.visible = true;
  }
}

export default Beam